import { Head, usePage } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreditCard, ShieldCheck } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface OrderData {
  id: number
  total: number
  vat: number
  grandTotal: number
}

interface Props extends Omit<AppShellProps, 'children'> {
  order: OrderData
  urls: AppShellProps['urls']
  translations: AppShellProps['translations'] & {
    checkout: string
    paymentInfo: string
    orderDetails: string
    orderId: string
    orderSubtotal: string
    vat: string
    grandTotal: string
    paymentTotal: string
    submit: string
    sar: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Checkout(props: Props) {
  const { order, urls, translations: t, ...shell } = props
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.checkout} />

      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-[#37352F]">{t.checkout}</h1>
          <p className="mt-1 text-[#91918E]">{t.paymentInfo}</p>
        </div>

        {flash?.alert && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {flash.alert}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_1.5fr]">
          {/* Order summary */}
          <Card className="border-[#E8E8E5]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base text-[#37352F]">
                <ShieldCheck className="h-4 w-4 text-[#2383E2]" />
                {t.orderDetails}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm">
                <li className="flex justify-between text-[#37352F]">
                  <span className="text-[#91918E]">{t.orderId}:</span>
                  <span className="font-medium">{order.id}</span>
                </li>
                <li className="flex justify-between text-[#37352F]">
                  <span className="text-[#91918E]">{t.orderSubtotal}:</span>
                  <span className="font-medium">
                    {order.total} {t.sar}
                  </span>
                </li>
                <li className="flex justify-between text-[#37352F]">
                  <span className="text-[#91918E]">{t.vat}:</span>
                  <span className="font-medium">
                    {order.vat} {t.sar}
                  </span>
                </li>
                <li className="flex justify-between border-t border-[#E8E8E5] pt-3">
                  <span className="font-semibold text-[#37352F]">{t.grandTotal}:</span>
                  <span className="font-bold text-[#2383E2]">
                    {order.grandTotal} {t.sar}
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Payment form */}
          <Card className="border-[#E8E8E5]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base text-[#37352F]">
                <CreditCard className="h-4 w-4 text-[#2383E2]" />
                {t.paymentTotal}:{' '}
                <span className="text-[#2383E2]">
                  {order.grandTotal} {t.sar}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Tap payment element container */}
              <div
                id="element-container"
                className="min-h-[200px] rounded-lg border border-[#E8E8E5] p-4"
              />
              <div
                id="error-handler"
                role="alert"
                className="mt-2 text-sm text-red-600"
              />
              <div className="mt-4 text-center">
                <button
                  id="tap-btn"
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#2383E2] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#1b6ec2]"
                >
                  <CreditCard className="h-4 w-4" />
                  {t.submit}
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-[#91918E]">
                Payment processed securely via Tap Payments
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}
